import { useCallback, useEffect, useMemo, useState } from "react";
import Segmented from "@/components/Segmented";
import { Loader2, Download, PlugZap, PauseCircle, PlayCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi, opsDownload, money } from "./lib/api";

const CHIPS = [
  ["all", "Everything", (r) => r.status !== "paid"],
  ["current", "Current", (r) => r.bucket === "current"],
  ["1_30", "1–30", (r) => r.bucket === "1_30"],
  ["31_60", "31–60", (r) => r.bucket === "31_60"],
  ["61_90", "61–90", (r) => r.bucket === "61_90"],
  ["over_90", "90+", (r) => r.bucket === "over_90"],
  ["retention", "Retention", (r) => r.bucket === "retention"],
  ["follow_up", "Follow-up due", (r) => r.follow_up_due],
  ["paused", "Paused", (r) => r.paused],
  ["promise", "Promise broken", (r) => r.promise_broken],
  ["paid", "Paid", (r) => r.status === "paid"],
];

const BUCKET_TONE = {
  current: "border-slate-200 bg-slate-50 text-slate-600",
  "1_30": "border-amber-200 bg-amber-50 text-amber-700",
  "31_60": "border-orange-200 bg-orange-50 text-orange-700",
  "61_90": "border-rose-200 bg-rose-50 text-rose-700",
  over_90: "border-rose-300 bg-rose-100 text-rose-800",
  retention: "border-sky-200 bg-sky-50 text-sky-700",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

/** FIN-5 — the receivables ledger. Every submitted application is money owed to us. */
export default function ReceivablesPage() {
  const [data, setData] = useState(null);
  const [chip, setChip] = useState("all");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try { setData(await opsApi.arDashboard()); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load receivables"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((CHIPS.find(([k]) => k === chip) || CHIPS[0])[2]);
  }, [data, chip]);

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-20 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading receivables…
    </div>;
  }
  const s = data.summary;
  const counts = Object.fromEntries(CHIPS.map(([k, , t]) => [k, data.rows.filter(t).length]));

  return (
    <div className="page-body space-y-5" data-testid="ar-page">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[["Outstanding", money(s.outstanding), "ar-kpi-outstanding"],
          ["Open invoices", `${s.invoices}`, "ar-kpi-invoices"],
          ["Over 60 days", money(s.over_60), "ar-kpi-over60"],
          ["Retention only", money(s.retention), "ar-kpi-retention"],
          ["Follow-ups due", `${s.follow_up_due}`, "ar-kpi-followups"]].map(([l, v, tid]) => (
          <div key={l} className="rounded-lg border border-slate-200 bg-white p-4"
               data-testid={tid}>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{l}</p>
            <p className="stat-num text-2xl font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="ar-chips">
        <Segmented
          options={CHIPS.map(([key, label]) => ({ key, label, count: counts[key] ?? 0,
                                                  testId: `ar-chip-${key}` }))}
          value={chip}
          onChange={setChip}
        />
        <Button variant="outline" size="sm" className="ml-auto" data-testid="ar-export"
                onClick={() => opsDownload("/ar/export", "accounts-receivable.csv")
                  .catch(() => toast.error("That export could not be built"))}>
          <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
        </Button>
      </div>

      {data.by_gc.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="ar-by-gc">
          {data.by_gc.slice(0, 8).map((g) => (
            <div key={g.gc} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <p className="text-xs font-medium text-slate-800">{g.gc}</p>
              <p className="text-xs text-slate-500">
                {money(g.amount)} · {g.count} inv · oldest {g.oldest}d
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
           data-testid="ar-not-wired">
        <p className="flex items-center gap-2 text-xs font-semibold text-amber-900">
          <PlugZap className="h-3.5 w-3.5" /> Not connected yet
        </p>
        {data.not_wired.map((n, i) => (
          <p key={i} className="text-xs text-amber-900">· {n}</p>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white"
           data-testid="ar-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left">Invoice</th>
              <th className="px-3 py-2 text-left">Job</th>
              <th className="px-3 py-2 text-left">GC</th>
              <th className="px-3 py-2 text-right">Invoiced</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-left">Due</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2 text-left">Bucket</th>
              <th className="px-3 py-2 text-left">Next step</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} onClick={() => setOpen(r.id)}
                  data-testid={`ar-row-${r.invoice_number}`}
                  className={`cursor-pointer hover:bg-slate-50 ${
                    r.promise_broken ? "bg-rose-50/60" : ""}`}>
                <td className="px-3 py-2">
                  <p className="font-medium text-slate-900">{r.invoice_number}</p>
                  <p className="text-[11px] text-slate-400">{r.invoice_date}</p>
                </td>
                <td className="px-3 py-2 text-xs">
                  <span className="font-mono text-slate-500">{r.job_number || "—"}</span>
                  <p className="max-w-[12rem] truncate text-slate-700">{r.project_name}</p>
                </td>
                <td className="max-w-[10rem] truncate px-3 py-2 text-xs text-slate-600">
                  {r.gc || "—"}
                </td>
                <td className="px-3 py-2 text-right text-xs">{money(r.original_amount)}</td>
                <td className="px-3 py-2 text-right text-xs">{money(r.amount_paid)}</td>
                <td className="px-3 py-2 text-right text-xs font-semibold">
                  {money(r.balance_due)}
                </td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.due_date}</td>
                <td className="px-3 py-2 text-right text-xs">{r.days_outstanding || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] ${
                    BUCKET_TONE[r.bucket] || BUCKET_TONE.current}`}>
                    {r.bucket_label}
                  </span>
                </td>
                <td className="px-3 py-2 text-[11px]">
                  {r.paused ? (
                    <span className="text-slate-500">
                      paused — {r.pause_reason_label}
                      {r.promise_date ? ` (${r.promise_date})` : ""}
                    </span>
                  ) : (
                    <span className={r.follow_up_due ? "font-medium text-amber-700"
                      : "text-slate-500"}>
                      {r.next_follow_up ? `${r.next_follow_up} — ${r.next_follow_up_label}`
                        : r.next_follow_up_label}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-sm text-slate-500">
                Nothing here. Invoices appear the moment an application for payment is submitted.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && <ArDrawer id={open} onClose={() => setOpen(null)} onSaved={load} />}
    </div>
  );
}

function ArDrawer({ id, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [pay, setPay] = useState({ amount: "", paid_on: "", reference: "", method: "check",
                                   short_pay_reason: "" });
  const [note, setNote] = useState("");
  const [pause, setPause] = useState({ reason: "promise_to_pay", promise_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setD(await opsApi.arInvoice(id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load that invoice"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    setBusy(true);
    try { setD(await fn()); toast.success(ok); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="ar-drawer">
        {!d ? (
          <div className="flex items-center gap-2 px-6 py-16 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="sticky top-0 flex items-start gap-3 border-b border-slate-200 bg-white px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">
                  {d.job_number} · {d.gc}
                </p>
                <h2 className="text-lg font-semibold text-slate-900" data-testid="ar-drawer-title">
                  {d.invoice_number} — {money(d.balance_due)} outstanding
                </h2>
                <p className="text-xs text-slate-500">
                  {d.status_label} · {d.bucket_label} · due {d.due_date} ·{" "}
                  {d.days_outstanding} days out
                </p>
              </div>
              <button onClick={onClose} data-testid="ar-drawer-close"
                      className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-900">Apply a payment</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Input type="number" step="0.01" placeholder="Amount" value={pay.amount}
                         data-testid="ar-pay-amount" className="h-8 text-xs"
                         onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} />
                  <Input type="date" value={pay.paid_on} className="h-8 text-xs"
                         data-testid="ar-pay-date"
                         onChange={(e) => setPay((p) => ({ ...p, paid_on: e.target.value }))} />
                  <Input placeholder="Check / ACH reference" value={pay.reference}
                         data-testid="ar-pay-ref" className="h-8 text-xs"
                         onChange={(e) => setPay((p) => ({ ...p, reference: e.target.value }))} />
                  <Input placeholder="Short-pay reason (if any)" value={pay.short_pay_reason}
                         data-testid="ar-pay-short" className="h-8 text-xs"
                         onChange={(e) => setPay((p) => ({ ...p,
                                                           short_pay_reason: e.target.value }))} />
                </div>
                <Button size="sm" className="mt-2" disabled={busy || !pay.amount}
                        data-testid="ar-pay-apply"
                        onClick={() => run(() => opsApi.arPayment(id, {
                          ...pay, amount: Number(pay.amount) }), "Payment applied")}>
                  Apply
                </Button>
                {d.payments.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid="ar-payments">
                    {d.payments.map((p) => (
                      <p key={p.id} className="text-[11px] text-slate-600">
                        {p.paid_on} · {money(p.amount)} · {p.method} {p.reference}
                        {p.retention_withheld ? ` · retention withheld ${money(p.retention_withheld)}` : ""}
                        {p.short_pay_reason ? ` · ${p.short_pay_reason}` : ""}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-900">Follow-up</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {d.paused ? `Paused — ${d.pause_reason_label}${d.promise_date
                    ? `, promised ${d.promise_date}` : ""}`
                    : `Next: ${d.next_follow_up || "—"} ${d.next_follow_up_label}`}
                </p>
                {d.paused ? (
                  <Button size="sm" variant="outline" className="mt-2" disabled={busy}
                          data-testid="ar-resume"
                          onClick={() => run(() => opsApi.arResume(id), "Follow-up resumed")}>
                    <PlayCircle className="mr-1.5 h-3.5 w-3.5" /> Resume
                  </Button>
                ) : (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <select value={pause.reason} data-testid="ar-pause-reason"
                            onChange={(e) => setPause((p) => ({ ...p, reason: e.target.value }))}
                            className="h-8 rounded-md border border-slate-200 px-2 text-xs">
                      {d.pause_reasons.map((r) => (
                        <option key={r.key} value={r.key}>{r.label}</option>
                      ))}
                    </select>
                    <Input type="date" value={pause.promise_date} className="h-8 w-36 text-xs"
                           data-testid="ar-pause-date"
                           onChange={(e) => setPause((p) => ({ ...p,
                                                               promise_date: e.target.value }))} />
                    <Input placeholder="Note" value={pause.note} className="h-8 flex-1 text-xs"
                           data-testid="ar-pause-note"
                           onChange={(e) => setPause((p) => ({ ...p, note: e.target.value }))} />
                    <Button size="sm" variant="outline" disabled={busy} data-testid="ar-pause"
                            onClick={() => run(() => opsApi.arPause(id, pause), "Follow-up paused")}>
                      <PauseCircle className="mr-1.5 h-3.5 w-3.5" /> Pause
                    </Button>
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-900">Collection notes</p>
                <div className="mt-2 flex gap-2">
                  <Textarea value={note} rows={2} className="text-xs" data-testid="ar-note-input"
                            placeholder="GC advised waiting on owner payment"
                            onChange={(e) => setNote(e.target.value)} />
                  <Button size="sm" variant="outline" disabled={busy || !note.trim()}
                          data-testid="ar-note-add"
                          onClick={() => run(async () => {
                            const r = await opsApi.arNote(id, note); setNote(""); return r;
                          }, "Note added")}>Add</Button>
                </div>
                <div className="mt-2 space-y-1" data-testid="ar-notes">
                  {d.notes.map((n) => (
                    <div key={n.id} className="rounded bg-slate-50 px-2 py-1 text-[11px]">
                      <p className="text-slate-800">{n.text}</p>
                      <p className="text-slate-500">{n.by} · {(n.at || "").slice(0, 16)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200">
                <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Timeline
                </p>
                <div data-testid="ar-timeline">
                  {d.timeline.map((t, i) => (
                    <div key={i} className="flex flex-wrap gap-2 px-3 py-1.5 text-[11px]">
                      <span className="font-mono text-slate-400">{(t.at || "").slice(0, 16)}</span>
                      <span className="font-medium text-slate-800">{t.what}</span>
                      <span className="text-slate-600">{t.detail}</span>
                      <span className="ml-auto text-slate-400">{t.by}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
